import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import SalesModal from '../components/sales/SalesModal';
import DeleteConfirmModal from '../components/shared/ConfirmDeleteModal';

const Sales = () => {
    const { isDark } = useTheme();
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingSale, setEditingSale] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Filters
    const [filters, setFilters] = useState({
        driver_id: '',
        car_id: '',
        status: '',
        date_from: '',
        date_to: '',
        year: '' // Added year filter
    });

    const years = [2020, 2021, 2022, 2023, 2024, 2025];

    // Dropdown data
    const [drivers, setDrivers] = useState([]);
    const [cars, setCars] = useState([]);

    const fetchDrivers = useCallback(async () => {
        try {
            const response = await fetch('/api/v1/drivers');
            const data = await response.json();
            setDrivers(data);
        } catch (error) {
            console.error('Error fetching drivers:', error);
        }
    }, []);

    const fetchCars = useCallback(async () => {
        try {
            const response = await fetch('/api/v1/cars');
            const data = await response.json();
            setCars(data);
        } catch (error) {
            console.error('Error fetching cars:', error);
        }
    }, []);

    const fetchSales = useCallback(async (filterParams = {}) => {
        setLoading(true);
        try {
            // Build query string from filters
            const queryParams = new URLSearchParams();
            if (filterParams.driver_id) queryParams.append('driver_id', filterParams.driver_id);
            if (filterParams.car_id) queryParams.append('car_id', filterParams.car_id);
            if (filterParams.year) queryParams.append('year', filterParams.year); // Pass year param

            // Note: date and status filtering might happen client-side if backend doesn't support it yet

            const response = await fetch(`/api/v1/sales?${queryParams.toString()}`, {
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            const data = await response.json();
            setSales(data);
        } catch (error) {
            console.error('Error fetching sales:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial load - only static data
    useEffect(() => {
        fetchDrivers();
        fetchCars();
    }, [fetchDrivers, fetchCars]);

    // Refetch when filters change (including initial mount)
    useEffect(() => {
        fetchSales(filters);
    }, [
        filters.driver_id,
        filters.car_id,
        filters.year,
        filters.status,
        filters.date_from,
        filters.date_to,
        fetchSales
    ]);

    const handleDelete = async (id) => {
        try {
            await fetch(`/api/v1/sales/${id}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                },
            });
            setSales(sales.filter(s => s.id !== id));
            setDeleteConfirm(null);
        } catch (error) {
            console.error('Error deleting sale:', error);
        }
    };

    const handleSave = (savedSale) => {
        if (editingSale) {
            setSales(sales.map(s => s.id === savedSale.id ? savedSale : s));
        } else {
            setSales([savedSale, ...sales]);
        }
        setShowModal(false);
        setEditingSale(null);
        fetchSales(filters); // Refresh to get associated data if needed
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleYearFilter = (year) => {
        setFilters(prev => ({
            ...prev,
            year: year
        }));
    };

    // Apply client-side filters for properties not handled by backend
    const filteredSales = sales.filter(sale => {
        if (filters.status && sale.status !== filters.status) return false;
        if (filters.date_from && new Date(sale.date_received) < new Date(filters.date_from)) return false;
        if (filters.date_to && new Date(sale.date_received) > new Date(filters.date_to)) return false;

        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const driverName = sale.driver ? `${sale.driver.first_name} ${sale.driver.last_name}`.toLowerCase() : '';
            const carDetails = sale.car ? `${sale.car.car_number} ${sale.car.model}`.toLowerCase() : '';
            const notes = sale.notes ? sale.notes.toLowerCase() : '';
            const amount = sale.amount.toString();

            return driverName.includes(searchLower) ||
                carDetails.includes(searchLower) ||
                notes.includes(searchLower) ||
                amount.includes(searchLower);
        }

        return true;
    });

    // Calculate totals
    const totalAmount = filteredSales.reduce((sum, sale) => sum + (parseFloat(sale.amount) || 0), 0);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const styles = {
        container: {
            padding: '1rem 0',
            background: isDark ? '#0a0a0a' : '#f8f9fa',
            minHeight: '100vh',
            paddingBottom: '80px', // Extra space for mobile considerations
        },
        header: {
            marginBottom: '2rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem',
        },
        title: {
            fontSize: '2rem',
            fontWeight: 'bold',
            color: isDark ? '#ffffff' : '#1f2937',
        },
        statsCard: {
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            padding: '1.5rem',
            borderRadius: '1rem',
            color: 'white',
            boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
            minWidth: isMobile ? '100%' : '200px',
            flex: isMobile ? '1 1 100%' : '0 1 auto',
        },
        statsLabel: {
            fontSize: '0.875rem',
            opacity: 0.9,
            marginBottom: '0.25rem',
        },
        statsValue: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
        },
        yearFilterContainer: {
            padding: '0 1rem',
            marginBottom: '1rem',
        },
        yearFilterTitle: {
            fontSize: '1.25rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            color: isDark ? '#fff' : '#1f2937',
        },
        yearLinks: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
            alignItems: 'center',
        },
        yearLink: (isActive) => ({
            cursor: 'pointer',
            fontSize: '1rem',
            color: isActive ? '#2563eb' : (isDark ? '#9ca3af' : '#6b7280'),
            textDecoration: isActive ? 'underline' : 'none',
            fontWeight: isActive ? 600 : 400,
        }),
        filterBar: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            padding: '1.5rem',
            borderRadius: '1rem',
            marginBottom: '2rem',
            marginLeft: '1rem',
            marginRight: '1rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            alignItems: 'end',
        },
        filterGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
        },
        label: {
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            color: isDark ? '#9ca3af' : '#6b7280',
            letterSpacing: '0.05em',
        },
        select: {
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#111827' : '#f9fafb',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
            width: '100%',
        },
        input: {
            padding: '0.75rem',
            borderRadius: '0.5rem',
            border: `1px solid ${isDark ? '#374151' : '#d1d5db'}`,
            background: isDark ? '#111827' : '#f9fafb',
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '0.875rem',
            width: '100%',
        },
        addButton: {
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)',
            transition: 'transform 0.2s',
            width: isMobile ? '100%' : 'auto',
        },
        tableContainer: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            marginLeft: isMobile ? '0' : '1rem',
            marginRight: isMobile ? '0' : '1rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            overflowX: 'auto',
            background: isMobile ? 'transparent' : (isDark ? '#1e1e1e' : '#ffffff'),
            boxShadow: isMobile ? 'none' : (isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'),
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
        },
        th: {
            textAlign: 'left',
            padding: '1rem',
            borderBottom: `2px solid ${isDark ? '#333' : '#e5e7eb'}`,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDark ? '#9ca3af' : '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
        },
        td: {
            padding: '1rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            color: isDark ? '#d1d5db' : '#374151',
            fontSize: '0.875rem',
        },
        statusBadge: (status) => ({
            display: 'inline-block',
            padding: '0.25rem 0.75rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            background: status === 'RECEIVED' ? 'rgba(16, 185, 129, 0.2)' :
                status === 'SENT_BY_DRIVER' ? 'rgba(59, 130, 246, 0.2)' :
                    'rgba(245, 158, 11, 0.2)',
            color: status === 'RECEIVED' ? '#10b981' :
                status === 'SENT_BY_DRIVER' ? '#3b82f6' :
                    '#f59e0b',
        }),
        actionButton: {
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            border: 'none',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontWeight: 600,
            marginRight: '0.5rem',
            transition: '0.2s',
        },
        editButton: {
            background: isDark ? '#1e40af' : '#3b82f6',
            color: 'white',
        },
        deleteButton: {
            background: isDark ? '#991b1b' : '#ef4444',
            color: 'white',
        },
        emptyState: {
            textAlign: 'center',
            padding: '4rem 2rem',
            color: isDark ? '#9ca3af' : '#6b7280',
        },
        // Mobile Card Styles
        card: {
            background: isDark ? '#1e1e1e' : '#ffffff',
            borderRadius: '1rem',
            padding: '1.25rem',
            marginBottom: '1rem',
            boxShadow: isDark
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            margin: '0 1rem 1rem 1rem',
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '1rem',
            borderBottom: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
            paddingBottom: '0.75rem',
        },
        cardRow: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
        },
        cardLabel: {
            color: isDark ? '#9ca3af' : '#6b7280',
            fontWeight: 500,
        },
        cardValue: {
            color: isDark ? '#d1d5db' : '#374151',
            fontWeight: 600,
            textAlign: 'right',
        },
    };

    const renderMobileCard = (sale) => (
        <div key={sale.id} style={styles.card}>
            <div style={styles.cardHeader}>
                <div>
                    <div style={{ fontWeight: 'bold', fontSize: '1rem', color: isDark ? '#fff' : '#1f2937' }}>
                        {sale.driver ? `${sale.driver.first_name} ${sale.driver.last_name}` : 'N/A'}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: isDark ? '#9ca3af' : '#6b7280' }}>
                        {sale.car ? `${sale.car.car_number}` : 'N/A'}
                    </div>
                </div>
                <span style={styles.statusBadge(sale.status)}>
                    {sale.status || 'YET_TO_SEND'}
                </span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Date</span>
                <span style={styles.cardValue}>{sale.date_received}</span>
            </div>

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Amount</span>
                <span style={{ ...styles.cardValue, color: '#10b981', fontSize: '1.1rem' }}>
                    GHS {parseFloat(sale.amount).toFixed(2)}
                </span>
            </div>

            {sale.amount_expected && (
                <div style={styles.cardRow}>
                    <span style={styles.cardLabel}>Expected</span>
                    <span style={styles.cardValue}>GHS {parseFloat(sale.amount_expected).toFixed(2)}</span>
                </div>
            )}

            <div style={styles.cardRow}>
                <span style={styles.cardLabel}>Mode</span>
                <span style={styles.cardValue}>{sale.payment_mode || 'N/A'}</span>
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                <button
                    onClick={() => {
                        setEditingSale(sale);
                        setShowModal(true);
                    }}
                    style={{ ...styles.actionButton, ...styles.editButton, flex: 1, textAlign: 'center' }}
                >
                    Edit
                </button>
                <button
                    onClick={() => setDeleteConfirm(sale)}
                    style={{ ...styles.actionButton, ...styles.deleteButton, flex: 1, textAlign: 'center' }}
                >
                    Delete
                </button>
            </div>
        </div>
    );

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <h1 style={styles.title}>Sales</h1>
                    <span style={{
                        background: isDark ? '#374151' : '#e5e7eb',
                        color: isDark ? '#d1d5db' : '#374151',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.875rem',
                        fontWeight: 600
                    }}>
                        {filteredSales.length}
                    </span>
                </div>

                <div style={styles.statsCard}>
                    <div style={styles.statsLabel}>Total Revenue</div>
                    <div style={styles.statsValue}>
                        GHS {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                </div>

                <button
                    onClick={() => {
                        setEditingSale(null);
                        setShowModal(true);
                    }}
                    style={styles.addButton}
                >
                    + New Sale
                </button>
            </div>

            {/* Year Filter UI */}
            <div style={styles.yearFilterContainer}>
                <h3 style={styles.yearFilterTitle}>Filter Sales Records By Year</h3>
                <div style={styles.yearLinks}>
                    {years.map(year => (
                        <span
                            key={year}
                            onClick={() => handleYearFilter(year)}
                            style={styles.yearLink(filters.year === year)}
                        >
                            {year}_records
                        </span>
                    ))}
                    {filters.year && (
                        <span
                            onClick={() => handleYearFilter('')}
                            style={{ ...styles.yearLink(false), color: '#ef4444', textDecoration: 'underline' }}
                        >
                            Clear Filter
                        </span>
                    )}
                </div>
            </div>

            <div style={styles.filterBar}>
                <div style={styles.filterGroup}>
                    <label style={styles.label}>Search</label>
                    <input
                        type="text"
                        placeholder="Driver, Car, Amount..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.input}
                    />
                </div>
                <div style={styles.filterGroup}>
                    <label style={styles.label}>Date From</label>
                    <input
                        type="date"
                        name="date_from"
                        value={filters.date_from}
                        onChange={handleFilterChange}
                        style={styles.input}
                    />
                </div>
                <div style={styles.filterGroup}>
                    <label style={styles.label}>Date To</label>
                    <input
                        type="date"
                        name="date_to"
                        value={filters.date_to}
                        onChange={handleFilterChange}
                        style={styles.input}
                    />
                </div>
                <div style={styles.filterGroup}>
                    <label style={styles.label}>Driver</label>
                    <select
                        name="driver_id"
                        value={filters.driver_id}
                        onChange={(e) => {
                            handleFilterChange(e);
                        }}
                        style={styles.select}
                    >
                        <option value="">All Drivers</option>
                        {drivers.map(driver => (
                            <option key={driver.id} value={driver.id}>
                                {driver.first_name} {driver.last_name} - {driver.car_number || 'No Car'}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={styles.filterGroup}>
                    <label style={styles.label}>Car</label>
                    <select
                        name="car_id"
                        value={filters.car_id}
                        onChange={(e) => {
                            handleFilterChange(e);
                        }}
                        style={styles.select}
                    >
                        <option value="">All Cars</option>
                        {cars.map(car => (
                            <option key={car.id} value={car.id}>
                                {car.car_number} - {car.model} - {car.driver_name || 'No Driver'}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={styles.filterGroup}>
                    <label style={styles.label}>Status</label>
                    <select
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                        style={styles.select}
                    >
                        <option value="">All Statuses</option>
                        <option value="YET_TO_SEND">Yet to Send</option>
                        <option value="SENT_BY_DRIVER">Sent by Driver</option>
                        <option value="RECEIVED">Received</option>
                    </select>
                </div>
                <div style={styles.filterGroup}>
                    <button
                        onClick={() => {
                            setFilters({
                                driver_id: '',
                                car_id: '',
                                status: '',
                                date_from: '',
                                date_to: '',
                                year: '' // Reset year too
                            });
                            // Note: We don't need a page reload, the state update triggers refetch
                        }}
                        style={{
                            ...styles.actionButton,
                            background: isDark ? '#374151' : '#e5e7eb',
                            color: isDark ? '#d1d5db' : '#374151',
                            width: '100%',
                            height: '42px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            <div style={styles.tableContainer}>
                {loading ? (
                    <div style={{ ...styles.emptyState, minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        Loading sales data...
                    </div>
                ) : filteredSales.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💸</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                            No sales records found
                        </div>
                        <div>
                            Try adjusting your filters or add a new sale
                        </div>
                    </div>
                ) : isMobile ? (
                    <div>
                        {filteredSales.map(renderMobileCard)}
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Received Date</th>
                                <th style={styles.th}>Expected Date</th>
                                <th style={styles.th}>Driver</th>
                                <th style={styles.th}>Car</th>
                                <th style={styles.th}>Amount Received</th>
                                <th style={styles.th}>Amount Expected</th>
                                <th style={styles.th}>Payment Mode</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredSales.map((sale) => (
                                <tr key={sale.id}>
                                    <td style={styles.td}>{sale.date_received}</td>
                                    <td style={styles.td}>{sale.expected_date || '-'}</td>
                                    <td style={styles.td}>
                                        {sale.driver ? `${sale.driver.first_name} ${sale.driver.last_name}` : 'N/A'}
                                    </td>
                                    <td style={styles.td}>
                                        {sale.car ? `${sale.car.car_number} - ${sale.car.model}` : 'N/A'}
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                        GHS {parseFloat(sale.amount).toFixed(2)}
                                    </td>
                                    <td style={styles.td}>
                                        {sale.amount_expected ? `GHS ${parseFloat(sale.amount_expected).toFixed(2)}` : '-'}
                                    </td>
                                    <td style={styles.td}>{sale.payment_mode || 'N/A'}</td>
                                    <td style={styles.td}>
                                        <span style={styles.statusBadge(sale.status)}>
                                            {sale.status || 'YET_TO_SEND'}
                                        </span>
                                    </td>
                                    <td style={styles.td}>
                                        <button
                                            onClick={() => {
                                                setEditingSale(sale);
                                                setShowModal(true);
                                            }}
                                            style={{ ...styles.actionButton, ...styles.editButton }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm(sale)}
                                            style={{ ...styles.actionButton, ...styles.deleteButton }}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <SalesModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setEditingSale(null);
                }}
                sale={editingSale}
                onSave={handleSave}
                drivers={drivers}
                cars={cars}
            />

            <DeleteConfirmModal
                isOpen={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                // We need to pass a "name" or something for the prompt
                title="Delete Sale"
                message={`Are you sure you want to delete this sale record? GHS ${deleteConfirm?.amount} on ${deleteConfirm?.date_received}`}
                onConfirm={() => handleDelete(deleteConfirm?.id)}
            />
        </div>
    );
};

export default Sales;
